import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import LeftSidebar from './LeftSidebar'
import RightSidebar from './RightSidebar'
import './mobileSidebar.css'
import { eventsService, Event } from '../../services/events.service'
import { useAuth } from '../../context/AuthContext'
import { useFeedUnreadDots } from '../../hooks/useFeedUnreadDots'
import { isAdminRole, isCoAdminOrAdmin } from '../../utils/roles'

type Props = {
  onClose: () => void
}

// add safe user parsing and initials helper
const getInitials = (name?: string) => {
  if (!name) return 'U'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export default function MobileSidebar({ onClose }: Props) {
  const location = useLocation()
  const feed = new URLSearchParams(location.search).get('feed')
  const isFeedHome =
    location.pathname === '/' &&
    (feed === null || feed === '' || feed === 'home')
  const isFeedBranch = location.pathname === '/' && feed === 'branch'
  const isFeedDepartment = location.pathname === '/' && feed === 'department'

  const { user } = useAuth()
  const {
    branchUnread,
    departmentUnread,
    onBranchNavClick,
    onDepartmentNavClick,
  } = useFeedUnreadDots()

  const [, setNewEmployeeEvents] = useState<Event[]>([])
  const isAdmin = isAdminRole(user?.role)
  const showCoAdminNav = isCoAdminOrAdmin(user?.role)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const all = await eventsService.getAll()
        if (!mounted) return
        setNewEmployeeEvents(all.filter((e) => e.isNewEmployee))
      } catch (err) {
        // ignore
      }
    }
    load()
    return () => { mounted = false }
  }, [])

  return (
    <div className="mobile-sidebar-overlay" onClick={onClose}>
      <aside className="mobile-sidebar" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-sidebar-header">
          <button className="mobile-close" onClick={onClose} aria-label="Close menu">×</button>
        </div>

        <div className="mobile-feed-nav">
          <Link
            to="/"
            className={`mobile-feed-link${isFeedHome ? ' active' : ''}`}
            onClick={onClose}
          >
            Home
          </Link>
          <Link
            to="/?feed=branch"
            className={`mobile-feed-link${isFeedBranch ? ' active' : ''}`}
            onClick={() => {
              onBranchNavClick()
              onClose()
            }}
          >
            My Branch
            {branchUnread && (
              <span className="mobile-feed-unread-dot" aria-hidden title="New posts" />
            )}
          </Link>
          <Link
            to="/?feed=department"
            className={`mobile-feed-link${isFeedDepartment ? ' active' : ''}`}
            onClick={() => {
              onDepartmentNavClick()
              onClose()
            }}
          >
            My Department
            {departmentUnread && (
              <span className="mobile-feed-unread-dot" aria-hidden title="New posts" />
            )}
          </Link>
        </div>

        {showCoAdminNav && (
          <div className="mobile-admin-actions">
            <Link to="/users" className="mobile-admin-link" onClick={onClose}>
              User management
            </Link>
            {isAdmin && (
              <>
                <Link to="/admin/analytics" className="mobile-admin-link" onClick={onClose}>
                  Open Analytics Dashboard
                </Link>
                <Link to="/admin/leads" className="mobile-admin-link" onClick={onClose}>
                  Vehicle Leads
                </Link>
                <Link to="/admin/activity" className="mobile-admin-link" onClick={onClose}>
                  Activity log
                </Link>
              </>
            )}
          </div>
        )}

        {/* grouped cards with consistent gap and equal widths */}
        <div className="mobile-cards">
          {/* Profile card uses shared sidebar-card class for same width as other cards */}
          <div className="sidebar-card mobile-profile-card">
            <div className="mobile-profile-avatar">
              {user?.profilePicture ? (
                <img src={user.profilePicture} alt={user.fullName || 'User'} />
              ) : (
                user ? getInitials(user.fullName) : 'U'
              )}
            </div>
            <div className="mobile-profile-info">
              <h3>{user?.fullName || 'User'}</h3>
              <p className="mobile-profile-role">{user?.role || ''}</p>
              <p className="mobile-profile-dept">{user?.department || ''}</p>
            </div>
          </div>

          {/* Welcome card */}
          {/* <div className="sidebar-card welcome-card mobile-welcome-card">
            <h4>Welcome to the Team!</h4>
            {newEmployeeEvents.length > 0 ? (
              newEmployeeEvents.map((ev) => (
                <div className="welcome-user" key={ev.id}>
                  <div className="welcome-avatar">
                    {ev.employeeName ? ev.employeeName.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <div className="welcome-info">
                    <span className="welcome-name">{ev.employeeName || 'New Employee'}</span>
                    <span className="welcome-dept">{ev.employeeDepartment ? `${ev.employeeDepartment} Department` : ''}</span>
                    <span className="welcome-date">Joined: {new Date(ev.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="welcome-user">
                <div className="welcome-avatar">
                  {user?.profilePicture ? (
                    <img src={user.profilePicture} alt={user.fullName || 'User'} />
                  ) : (
                    user?.fullName ? user.fullName.charAt(0).toUpperCase() : 'U'
                  )}
                </div>
                <div className="welcome-info">
                  <span className="welcome-name">{user?.fullName || 'User'}</span>
                  <span className="welcome-dept">{user?.department ? `${user.department} Department` : ''}</span>
                  <span className="welcome-date">Joined: Nov 2024</span>
                </div>
              </div>
            )}
          </div> */}
        </div>

        <div className="mobile-sidebar-body">
          <div className="mobile-left">
            <LeftSidebar />
          </div>

          <div className="mobile-right">
            <RightSidebar />
          </div>
        </div>
      </aside>
    </div>
  )
}